import type { ReactNode } from 'react';
import { COLORS } from '../../constants/theme';
import { formatNumber } from '../../utils/format';
import { formatEnumLabel } from '../../utils/format';
import { isIntelStale } from '../../utils/intelStaleness';
import { IntelFreshnessIndicator } from './IntelFreshnessIndicator';
import { ProtectedBadge } from '../ProtectedBadge';
import { isProtected } from '../../utils/protection';
import type { AttackType } from '../../types/attack';
import type { KingdomMember } from '../../types/magic';
import type { TargetIntelData } from '../../hooks/useTargetIntel';

interface WarRoomTargetCardProps {
  targetMember: KingdomMember | null;
  intel: TargetIntelData;
  onRunSohd: () => void;
  currentTick: number;
  ownNetworth: number;
  selectedAttackType: AttackType;
}

export function WarRoomTargetCard({
  targetMember,
  intel,
  onRunSohd,
  currentTick,
  ownNetworth,
  selectedAttackType,
}: WarRoomTargetCardProps) {
  const { sohdReport, sohdData } = intel;
  const stale = sohdReport ? isIntelStale(sohdReport.capturedAt) : false;
  const protectedTarget = !!targetMember && isProtected(targetMember.protectionExpiresAtTick, currentTick);

  const relativeNwPct =
    targetMember && ownNetworth > 0 ? Math.round((targetMember.networth / ownNetworth) * 100) : null;

  return (
    <div
      className="rounded p-3 space-y-2"
      style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
    >
      <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: COLORS.text.tertiary }}>
        Target
      </div>

      {targetMember ? (
        <div className="space-y-1">
          <div className="flex items-baseline gap-2">
            <span className="font-medium" style={{ color: COLORS.text.primary }}>
              {targetMember.provinceName}
            </span>
            <span className="text-xs" style={{ color: COLORS.text.muted }}>
              ({targetMember.location})
            </span>
            {protectedTarget && (
              <ProtectedBadge protectionExpiresAtTick={targetMember.protectionExpiresAtTick} currentTick={currentTick} />
            )}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs" style={{ color: COLORS.text.tertiary }}>
            {targetMember.race && (
              <span>{formatEnumLabel(targetMember.race)}</span>
            )}
            {Number.isFinite(targetMember.land) && targetMember.land > 0 && (
              <span>{formatNumber(targetMember.land)} acres</span>
            )}
            {Number.isFinite(targetMember.networth) && targetMember.networth > 0 && (
              <span style={{ color: COLORS.gold }}>
                {formatNumber(targetMember.networth)} nw
                {relativeNwPct !== null && ` (${relativeNwPct}% of yours)`}
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="text-sm" style={{ color: COLORS.text.muted }}>No target selected</div>
      )}

      {/* SOHD intel section */}
      <div
        className="pt-2 space-y-1"
        style={{ borderTop: `1px solid ${COLORS.border}` }}
      >
        <div className="flex items-center justify-between">
          <span className="text-xs" style={{ color: COLORS.text.tertiary }}>Total Home Defense</span>
          {sohdReport && (
            <IntelFreshnessIndicator capturedAt={sohdReport.capturedAt} />
          )}
        </div>

        {sohdData && sohdReport ? (
          <>
            <div className="text-sm font-medium" style={{ color: stale ? COLORS.warning : COLORS.text.primary }}>
              {formatNumber(sohdData.homeDefense)} DP
            </div>
            {/* SF-147 Component 7: defense-per-acre, computable client-side from data already on
                the page — no backend change needed. Same staleness flag as the raw homeDefense
                figure above (both derive from the same SOHD report). Guarded against a null
                targetMember or a zero-land edge case (can't divide by zero). */}
            {targetMember && targetMember.land > 0 && (
              <div className="text-xs" style={{ color: stale ? COLORS.warning : COLORS.text.tertiary }}>
                {(sohdData.homeDefense / targetMember.land).toFixed(2)} DPA
              </div>
            )}
            {stale && (
              <div
                className="text-xs rounded px-2 py-1"
                style={{
                  backgroundColor: '#2a2000',
                  border: `1px solid #5a4400`,
                  color: COLORS.warning,
                }}
              >
                Intel is stale — defense may have shifted. Consider re-running SOHD.
              </div>
            )}
          </>
        ) : (
          <div
            className="text-xs rounded px-2 py-1 flex items-center justify-between gap-2"
            style={{
              backgroundColor: '#2a0a0a',
              border: `1px solid #4a1a1a`,
              color: COLORS.negative,
            }}
          >
            <span>No Spy on Home Defense intel. Results will be unreliable.</span>
            {targetMember && (
              <button
                className="text-xs underline shrink-0"
                style={{ color: COLORS.link }}
                onClick={onRunSohd}
              >
                Run SOHD →
              </button>
            )}
          </div>
        )}
      </div>

      {/* SF-174 Component 4: attack-type-conditional stat panel — surfaces only the intel
          relevant to the currently selected Attack Type. Land/Networth/Relative NW% above and the
          Home Defense/SOHD block stay visible regardless of Attack Type. */}
      {targetMember && <AttackTypeStatPanel selectedAttackType={selectedAttackType} intel={intel} />}
    </div>
  );
}

function AttackTypeStatPanel({
  selectedAttackType,
  intel,
}: {
  selectedAttackType: AttackType;
  intel: TargetIntelData;
}) {
  switch (selectedAttackType) {
    case 'TRADITIONAL_MARCH':
    case 'CONQUEST':
      return null;
    case 'PLUNDER':
      return (
        <IntelPanelSection title="Plunder Preview" report={intel.cbReport} sourceName="Crystal Ball">
          {intel.cbData && (
            <>
              <StatRow label="Gold" value={formatNumber(intel.cbData.money)} />
              <StatRow label="Runes" value={formatNumber(intel.cbData.runes)} />
              <StatRow label="Food" value={formatNumber(intel.cbData.food)} />
            </>
          )}
        </IntelPanelSection>
      );
    case 'RAZE':
      return (
        <IntelPanelSection title="Building Counts" report={intel.surveyReport} sourceName="Survey">
          {intel.surveyData && (
            <div className="space-y-0.5">
              {Object.entries(intel.surveyData.buildings)
                .filter(([, count]) => count > 0)
                .map(([building, count]) => (
                  <StatRow key={building} label={formatEnumLabel(building)} value={formatNumber(count)} />
                ))}
            </div>
          )}
        </IntelPanelSection>
      );
    case 'LEARN':
      return (
        <IntelPanelSection title="Research Progress" report={intel.sosReport} sourceName="Spy on Sciences">
          {intel.sosData && (
            <div className="space-y-0.5">
              <StatRow label="Research Rate" value={formatEnumLabel(intel.sosData.researchRate)} />
              {intel.sosData.sciences.map((s) => (
                <StatRow key={s.scienceType} label={formatEnumLabel(s.scienceType)} value={formatNumber(s.booksKnown)} />
              ))}
            </div>
          )}
        </IntelPanelSection>
      );
    case 'MASSACRE':
      return (
        <div className="pt-2 space-y-1" style={{ borderTop: `1px solid ${COLORS.border}` }}>
          <div className="text-xs" style={{ color: COLORS.text.tertiary }}>Population at Risk</div>
          {intel.cbData ? (
            <StatRow label="Peasants" value={formatNumber(intel.cbData.peasants)} />
          ) : (
            <MissingIntelLine sourceName="Crystal Ball" />
          )}
          {intel.sotData ? (
            <>
              <StatRow label="Thieves" value={formatNumber(intel.sotData.thieves)} />
              <StatRow label="Wizards" value={formatNumber(intel.sotData.wizards)} />
            </>
          ) : (
            <MissingIntelLine sourceName="Spy on Throne" />
          )}
        </div>
      );
    default:
      return null;
  }
}

function IntelPanelSection({
  title,
  report,
  sourceName,
  children,
}: {
  title: string;
  report: { capturedAt: string } | null;
  sourceName: string;
  children: ReactNode;
}) {
  return (
    <div className="pt-2 space-y-1" style={{ borderTop: `1px solid ${COLORS.border}` }}>
      <div className="flex items-center justify-between">
        <span className="text-xs" style={{ color: COLORS.text.tertiary }}>{title}</span>
        {report && <IntelFreshnessIndicator capturedAt={report.capturedAt} />}
      </div>
      {report ? children : <MissingIntelLine sourceName={sourceName} />}
    </div>
  );
}

function MissingIntelLine({ sourceName }: { sourceName: string }) {
  return (
    <div
      className="text-xs rounded px-2 py-1"
      style={{ backgroundColor: '#2a0a0a', border: `1px solid #4a1a1a`, color: COLORS.negative }}
    >
      No {sourceName} intel.
    </div>
  );
}

function StatRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-xs">
      <span style={{ color: COLORS.text.tertiary }}>{label}</span>
      <span style={{ color: COLORS.text.secondary }}>{value}</span>
    </div>
  );
}
